import { apiClient } from '../api/client';

export const APTITUDE_ASSESSMENT_NAME = 'MindEdge Aptitude Test';

export const APTITUDE_SECTIONS = [
  { code: 'APT_QA', name: 'Quantitative Aptitude' },
  { code: 'APT_LR', name: 'Logical Reasoning' },
  { code: 'APT_VA', name: 'Verbal Ability' },
  { code: 'APT_DI', name: 'Data Interpretation' },
  { code: 'APT_AR', name: 'Abstract Reasoning' }
];

export const COGNITIVE_ASSESSMENT_NAME = 'NeuroPeak Cognitive Battery';

export const COGNITIVE_SECTIONS = [
  { code: 'COG_WM', name: 'Working Memory' },
  { code: 'COG_PS', name: 'Processing Speed' },
  { code: 'COG_FR', name: 'Fluid Reasoning' },
  { code: 'COG_AF', name: 'Attention & Focus' },
  { code: 'COG_SI', name: 'Spatial Intelligence' }
];

export const SKILL_ASSESSMENT_NAME = 'ProEdge Skill Assessment';

export const SKILL_SECTIONS = [
  { code: 'SKL_TK', name: 'Technical Knowledge' },
  { code: 'SKL_AT', name: 'Analytical Thinking' },
  { code: 'SKL_CS', name: 'Communication Skills' },
  { code: 'SKL_TL', name: 'Team & Leadership' },
  { code: 'SKL_PE', name: 'Professional Ethics' }
];

export const MEGA_ASSESSMENT_NAME = 'Apex All-in-One Mega Bundle';

export const MEGA_SECTIONS = [
  { code: 'MEG_QL', name: 'Quantitative & Logical' },
  { code: 'MEG_CB', name: 'Cognitive Battery' },
  { code: 'MEG_ST', name: 'Skill & Technical' },
  { code: 'MEG_VC', name: 'Verbal & Communication' },
  { code: 'MEG_DA', name: 'Data & Abstract Reasoning' },
  { code: 'MEG_CF', name: 'Coding Fundamentals' },
  { code: 'MEG_SJ', name: 'Situational Judgement' }
];

const hashString = (value) => {
  let h = 1779033703 ^ value.length;
  for (let i = 0; i < value.length; i += 1) {
    h = Math.imul(h ^ value.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (h >>> 0) || 1;
};

export const mulberry32 = (seed) => {
  let state = seed >>> 0;
  return function rand() {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export const seededShuffle = (list, seedText) => {
  const seeded = [...list];
  const random = mulberry32(hashString(seedText));
  for (let i = seeded.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [seeded[i], seeded[j]] = [seeded[j], seeded[i]];
  }
  return seeded;
};

export const buildAptitudeSeed = (userId) => (
  `${userId}_${Date.now()}_${Math.random().toString(36).slice(2)}`
);

export const startAptitudeAssessment = async ({ userId, sessionId, profilePhoto }) => {
  const response = await apiClient.post('/assessment/aptitude/start', { userId, sessionId, profilePhoto });
  return response.data;
};

export const submitAptitudeSection = async ({ sessionId, sectionCode, answers }) => {
  const response = await apiClient.post('/assessment/aptitude/submit-section', {
    sessionId,
    sectionCode,
    answers
  });
  return response.data;
};

export const completeAptitudeAssessment = async ({ sessionId, proctoringData, profilePhoto }) => {
  const response = await apiClient.post('/assessment/aptitude/complete', { sessionId, proctoringData, profilePhoto });
  // Assignment invite ref is one-time; clear it only after successful completion submit.
  sessionStorage.removeItem('assessment_ref');
  return response.data;
};

export const getAptitudeResult = async (sessionId) => {
  const response = await apiClient.get(`/assessment/aptitude/result/${sessionId}`);
  return response.data;
};

export const startCognitiveAssessment = async ({ userId, sessionId, profilePhoto }) => {
  const response = await apiClient.post('/assessment/cognitive/start', { userId, sessionId, profilePhoto });
  return response.data;
};

export const submitCognitiveSection = async ({ sessionId, sectionCode, answers }) => {
  const response = await apiClient.post('/assessment/cognitive/submit-section', {
    sessionId,
    sectionCode,
    answers
  });
  return response.data;
};

export const completeCognitiveAssessment = async ({ sessionId, proctoringData, profilePhoto }) => {
  const response = await apiClient.post('/assessment/cognitive/complete', { sessionId, proctoringData, profilePhoto });
  // Assignment invite ref is one-time; clear it only after successful completion submit.
  sessionStorage.removeItem('assessment_ref');
  return response.data;
};

export const getCognitiveResult = async (sessionId) => {
  const response = await apiClient.get(`/assessment/cognitive/result/${sessionId}`);
  return response.data;
};

export const startSkillAssessment = async ({ userId, sessionId, profilePhoto }) => {
  const response = await apiClient.post('/assessment/skill/start', { userId, sessionId, profilePhoto });
  return response.data;
};

export const submitSkillSection = async ({ sessionId, sectionCode, answers }) => {
  const response = await apiClient.post('/assessment/skill/submit-section', {
    sessionId,
    sectionCode,
    answers
  });
  return response.data;
};

export const completeSkillAssessment = async ({ sessionId, proctoringData, profilePhoto }) => {
  const response = await apiClient.post('/assessment/skill/complete', { sessionId, proctoringData, profilePhoto });
  // Assignment invite ref is one-time; clear it only after successful completion submit.
  sessionStorage.removeItem('assessment_ref');
  return response.data;
};

export const getSkillResult = async (sessionId) => {
  const response = await apiClient.get(`/assessment/skill/result/${sessionId}`);
  return response.data;
};

export const startMegaAssessment = async ({ userId, sessionId, profilePhoto }) => {
  const response = await apiClient.post('/assessment/mega/start', { userId, sessionId, profilePhoto });
  return response.data;
};

export const submitMegaSection = async ({ sessionId, sectionCode, answers }) => {
  const response = await apiClient.post('/assessment/mega/submit-section', {
    sessionId,
    sectionCode,
    answers
  });
  return response.data;
};

export const completeMegaAssessment = async ({ sessionId, proctoringData, profilePhoto }) => {
  const response = await apiClient.post('/assessment/mega/complete', { sessionId, proctoringData, profilePhoto });
  // Assignment invite ref is one-time; clear it only after successful completion submit.
  sessionStorage.removeItem('assessment_ref');
  return response.data;
};

export const getMegaResult = async (sessionId) => {
  const response = await apiClient.get(`/assessment/mega/result/${sessionId}`);
  return response.data;
};

export const flagMegaEvent = async ({ sessionId, eventType, timestamp }) => {
  const response = await apiClient.post('/assessment/mega/flag-event', {
    sessionId,
    eventType,
    timestamp
  });
  return response.data;
};
